// ponytail: v4 rewrite. Was v2 class extending MapLayer + withLeaflet HOC.
// Now function component using useMap + effects. Same public props/behavior.
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "./lib/leaflet-distortableimage";
import "./lib/leaflet-path-transform";

function getOptions(props) {
  const { url, editMode, onUpdate, opacity, ...rest } = props;
  return rest;
}

export default function ReactDistortableImageOverlay(props) {
  const map = useMap();
  const overlayRef = useRef(null);
  const translatePolygonRef = useRef(null);
  const translateCornersRef = useRef(null);

  const applyEditMode = (overlay, editMode) => {
    if (!overlay) return;
    switch (editMode) {
      case "rotate":
        overlay.editing.enable();
        overlay.editing._toggleRotateDistort();
        break;
      case "distort":
        overlay.editing.enable();
        break;
      case "translate":
        startTranslate(overlay);
        break;
      case "scale":
        overlay.editing.enable();
        overlay.editing._toggleScale();
        break;
      default:
        overlay.editing.disable();
    }
    if (overlay._image) overlay._image.style.opacity = props.opacity;
  };

  const startTranslate = (overlay) => {
    const corners = overlay.getCorners();
    // DistortableImageOverlay corner order differs from L.Polygon
    const polygonDragCorners = [corners[0], corners[1], corners[3], corners[2]];

    const poly = new L.Polygon(polygonDragCorners, {
      draggable: true,
      transform: true,
      color: "red",
      fill: true,
    });
    if (overlay._image) overlay._image.style.opacity = props.opacity;
    poly.transform.enable();

    poly.on("dragend", (event) => {
      const latlngs = event.target.getLatLngs()[0];
      const newImageCorners = [latlngs[0], latlngs[1], latlngs[3], latlngs[2]];
      setTimeout(() => props.onUpdate && props.onUpdate(newImageCorners), 10);
      map.removeLayer(overlay);

      const next = new L.DistortableImageOverlay(props.url, {
        corners: newImageCorners,
      });
      next.addTo(map);
      if (next._image) next._image.style.opacity = props.opacity;
      overlayRef.current = next;
      translateCornersRef.current = newImageCorners;
    });

    poly.addTo(map);
    translatePolygonRef.current = poly;
  };

  const endTranslate = () => {
    if (translatePolygonRef.current) {
      map.removeLayer(translatePolygonRef.current);
      translatePolygonRef.current = null;
    }
  };

  // Create/recreate overlay when url changes
  useEffect(() => {
    const overlay = new L.DistortableImageOverlay(
      props.url,
      getOptions(props)
    );
    L.DomEvent.on(
      overlay,
      "load",
      () => {
        if (overlay._image) overlay._image.style.opacity = props.opacity;
        applyEditMode(overlay, props.editMode);
      },
      overlay
    );
    overlay.on(
      "edit",
      (update) => {
        props.onUpdate && props.onUpdate(update.sourceTarget._corners);
      },
      overlay
    );
    overlay.addTo(map);
    overlayRef.current = overlay;

    return () => {
      endTranslate();
      if (overlayRef.current) {
        map.removeLayer(overlayRef.current);
        overlayRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.url]);

  // React to editMode changes without recreating overlay
  useEffect(() => {
    endTranslate();
    applyEditMode(overlayRef.current, props.editMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.editMode]);

  // React to opacity changes
  useEffect(() => {
    const overlay = overlayRef.current;
    if (overlay && overlay._image) {
      overlay._image.style.opacity = props.opacity;
    }
  }, [props.opacity]);

  return null;
}
